import { useEffect, useRef, useState, type FormEvent } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { DriveDocType, SourceKind } from '../contexts/RepoContext';
import { apiConfig } from '../authConfig';
import { pickDriveFolder, preloadGooglePicker } from '../utils/googlePicker';

interface Props {
    onClose: () => void;
    onAdded: () => void;
}

interface AddRepoResponse {
    repo: { repoId: string };
    filesEnqueued?: number;
    status?: string;
}

interface DriveDiscoverResponse {
    serviceAccountEmail: string;
    /** True when the indexer runs in Domain-Wide Delegation mode and admins
     *  don't have to share folders with the SA email. */
    dwd?: boolean;
    folders: Array<{ id: string; name: string; registeredRepoId: string | null }>;
    rootFolder: { id: string; name: string } | null;
    hint?: string;
}

interface DriveFolderListing {
    serviceAccountEmail: string;
    rootFolder: { id: string; name: string };
    folders: Array<{ id: string; name: string; registeredRepoId: string | null }>;
}

// Filter labels are origin-based — they describe what the file is in Drive,
// not how the reader renders it. A `.md` file uploaded to Drive sits in
// `text`, even though the viewer still renders it as markdown. Native
// Google Docs are the only thing in `docs`.
const ALL_DOC_TYPES: Array<{ key: DriveDocType; label: string; hint: string }> = [
    { key: 'docs', label: 'Google Docs (native)', hint: 'Google Docs created in Drive. Exported as Markdown for the reader.' },
    { key: 'sheets', label: 'Google Sheets', hint: 'Exported as CSV; rendered as a table.' },
    { key: 'slides', label: 'Google Slides', hint: 'Exported as plain text (titles + body).' },
    { key: 'pdf', label: 'PDFs', hint: 'Embedded viewer + extracted text for search.' },
    { key: 'text', label: 'Text & Markdown files', hint: 'Uploaded .md / .txt / .csv / .tsv (not native Google Docs).' }
];

/**
 * Add a new source: either a GitHub repo (existing flow) or a Google Drive
 * folder (new flow). Source kind is the first choice; subsequent inputs are
 * kind-specific.
 */
export function AddRepoModal({ onClose, onAdded }: Props) {
    const [kind, setKind] = useState<SourceKind>('github');

    // Shared
    const [sensitiveContentLock, setSensitiveContentLock] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // GitHub-only state
    const [repoId, setRepoId] = useState('');
    const [installationId, setInstallationId] = useState('');

    // Drive-only state
    const [driveAlias, setDriveAlias] = useState('');
    const [driveFolderId, setDriveFolderId] = useState('');
    const [driveFolderName, setDriveFolderName] = useState('');
    // Tracks whether the admin has typed in the display-name field. If they
    // haven't, picking a different folder via the Picker / browse list
    // *replaces* the auto-filled name (so it never lags behind the
    // selection). Once they edit it manually, we leave it alone.
    //
    // Mirror in a ref so async callbacks (Picker resolve) read the
    // CURRENT value rather than the closure-captured value at launch
    // time — otherwise editing the name while the Picker popup is open
    // can be silently overwritten on return.
    const [driveFolderNameDirty, setDriveFolderNameDirtyState] = useState(false);
    const driveFolderNameDirtyRef = useRef(false);
    const setDriveFolderNameDirty = (next: boolean) => {
        driveFolderNameDirtyRef.current = next;
        setDriveFolderNameDirtyState(next);
    };
    const [docTypeFilters, setDocTypeFilters] = useState<DriveDocType[]>(['all']);
    const [drive, setDrive] = useState<DriveDiscoverResponse | null>(null);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveError, setDriveError] = useState<string | null>(null);
    const [browseRoot, setBrowseRoot] = useState('');
    const [browseListing, setBrowseListing] = useState<DriveFolderListing | null>(null);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [browseError, setBrowseError] = useState<string | null>(null);
    const [pickerLaunching, setPickerLaunching] = useState(false);
    const [pickerError, setPickerError] = useState<string | null>(null);
    const pickerEnabled = apiConfig.googlePicker !== null;

    function switchKind(next: SourceKind) {
        if (next === kind) return;
        setKind(next);
        // Clear shared status banners; KEEP form fields so an admin can
        // toggle back and forth (e.g. to compare hints) without losing
        // typed values. State is reset on modal close, not on tab toggle.
        setError(null);
        setSuccess(null);
    }

    useEffect(() => {
        if (kind !== 'gdrive') return;
        let cancelled = false;
        setDriveLoading(true);
        setDriveError(null);
        (async () => {
            try {
                const r = await apiCall<DriveDiscoverResponse>('/manage/drive/folders');
                if (!cancelled) setDrive(r);
            } catch (err) {
                if (!cancelled) setDriveError(err instanceof ApiError ? err.message : (err as Error).message);
            } finally {
                if (!cancelled) setDriveLoading(false);
            }
        })();
        // Warm up the Picker scripts while the modal is open so the
        // click-to-launch path can call `requestAccessToken` synchronously
        // enough for the browser to treat the OAuth popup as
        // user-initiated. Errors are swallowed — they'll surface again
        // when the user actually clicks the button.
        if (pickerEnabled) {
            void preloadGooglePicker().catch(() => {});
        }
        return () => { cancelled = true; };
    }, [kind, pickerEnabled]);

    async function launchPicker() {
        if (!apiConfig.googlePicker) return;
        setPickerLaunching(true);
        setPickerError(null);
        try {
            const result = await pickDriveFolder(apiConfig.googlePicker);
            if (!result) return; // cancelled
            setDriveFolderId(result.id);
            // Read dirty status via the ref so an edit made while the
            // picker was open isn't clobbered by the closure-captured
            // value from before launch.
            if (!driveFolderNameDirtyRef.current) {
                const composed = result.driveName && result.driveName !== result.name
                    ? `${result.driveName} / ${result.name}`
                    : result.name;
                setDriveFolderName(composed);
            }
        } catch (err) {
            setPickerError(err instanceof Error ? err.message : String(err));
        } finally {
            setPickerLaunching(false);
        }
    }

    async function browseSubfolders() {
        const id = browseRoot.trim();
        if (!id) return;
        setBrowseLoading(true);
        setBrowseError(null);
        setBrowseListing(null);
        try {
            const r = await apiCall<DriveFolderListing>(`/manage/drive/folders?root=${encodeURIComponent(id)}`);
            setBrowseListing(r);
        } catch (err) {
            setBrowseError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setBrowseLoading(false);
        }
    }

    function toggleDocType(t: DriveDocType) {
        setDocTypeFilters((prev) => {
            if (t === 'all') return ['all'];
            const without = prev.filter((x) => x !== 'all');
            if (without.includes(t)) {
                const next = without.filter((x) => x !== t);
                return next.length === 0 ? ['all'] : next;
            }
            return [...without, t];
        });
    }

    async function handleGithubSubmit(e: FormEvent) {
        e.preventDefault();
        if (!repoId.match(/^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/)) {
            setError('repoId must be in the form `owner/repo`');
            return;
        }
        // installationId is optional — the backend falls back to the
        // org-level installation when omitted. Validate the format only
        // when an admin actually filled it in.
        const trimmed = installationId.trim();
        let installation: number | undefined;
        if (trimmed.length > 0) {
            const parsed = Number.parseInt(trimmed, 10);
            if (!Number.isFinite(parsed) || parsed <= 0) {
                setError('installationId must be a positive integer (or leave blank to use the org-level install)');
                return;
            }
            installation = parsed;
        }
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const body: {
                sourceKind: 'github';
                repoId: string;
                sensitiveContentLock: boolean;
                installationId?: number;
            } = {
                sourceKind: 'github',
                repoId,
                sensitiveContentLock
            };
            if (installation !== undefined) body.installationId = installation;
            const r = await apiCall<AddRepoResponse>('/manage/repos', {
                method: 'POST',
                body
            });
            setSuccess(`Added ${r.repo.repoId}. ${r.filesEnqueued ?? 0} files enqueued for indexing.`);
            onAdded();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleDriveSubmit(e: FormEvent) {
        e.preventDefault();
        const alias = driveAlias.trim().toLowerCase();
        if (!alias.match(/^[a-z0-9][a-z0-9._-]{0,99}$/)) {
            setError('Alias must be 1–100 lowercase chars (letters, digits, dot, hyphen, underscore).');
            return;
        }
        if (!driveFolderId.trim()) {
            setError('Folder ID is required.');
            return;
        }
        const effectiveFilters: DriveDocType[] =
            docTypeFilters.includes('all') || docTypeFilters.length === 0 ? ['all'] : docTypeFilters;
        setSubmitting(true);
        setError(null);
        setSuccess(null);
        try {
            const r = await apiCall<AddRepoResponse>('/manage/repos', {
                method: 'POST',
                body: {
                    sourceKind: 'gdrive',
                    repoId: `gdrive/${alias}`,
                    sensitiveContentLock,
                    driveSource: {
                        folderId: driveFolderId.trim(),
                        folderName: driveFolderName.trim() || undefined,
                        docTypeFilters: effectiveFilters
                    }
                }
            });
            setSuccess(`Added ${r.repo.repoId}. ${r.filesEnqueued ?? 0} files enqueued for indexing.`);
            onAdded();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="modal-overlay" onClick={onClose} role="presentation">
            <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <header className="modal-header">
                    <h2>Add source</h2>
                    <button className="btn btn--ghost" onClick={onClose} aria-label="Close">×</button>
                </header>

                <div className="modal-body">
                    <div className="form-group">
                        <span className="form-label">Source type</span>
                        <div className="source-kind-tabs">
                            <button
                                type="button"
                                className={'btn btn--ghost' + (kind === 'github' ? ' btn--active' : '')}
                                onClick={() => switchKind('github')}
                                disabled={submitting}
                            >
                                GitHub repository
                            </button>
                            <button
                                type="button"
                                className={'btn btn--ghost' + (kind === 'gdrive' ? ' btn--active' : '')}
                                onClick={() => switchKind('gdrive')}
                                disabled={submitting}
                            >
                                Google Drive folder
                            </button>
                        </div>
                    </div>

                    {kind === 'github' ? (
                        <form onSubmit={handleGithubSubmit}>
                            <label className="form-group">
                                <span className="form-label">repoId</span>
                                <input
                                    type="text"
                                    value={repoId}
                                    onChange={(e) => setRepoId(e.target.value)}
                                    placeholder="Bay-Area-Community-Resources/sf-docs"
                                    autoFocus
                                    required
                                />
                                <span className="form-hint">
                                    GitHub <code>owner/repo</code> form. The GitHub App must be installed on it.
                                </span>
                            </label>
                            <label className="form-group">
                                <span className="form-label">installationId <span className="form-hint">(optional)</span></span>
                                <input
                                    type="number"
                                    value={installationId}
                                    onChange={(e) => setInstallationId(e.target.value)}
                                    placeholder="leave blank to use the org-level install"
                                />
                                <span className="form-hint">
                                    Numeric ID from <code>github.com/organizations/&lt;org&gt;/settings/installations</code>.
                                    Leave blank to use the installation configured in Admin → Organization.
                                </span>
                            </label>
                            <SensitiveLockToggle value={sensitiveContentLock} onChange={setSensitiveContentLock} />
                            {error && <div className="form-error">{error}</div>}
                            {success && <div className="form-success">{success}</div>}
                            <footer className="modal-footer">
                                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
                                    {success ? 'Close' : 'Cancel'}
                                </button>
                                {!success && (
                                    <button type="submit" className="btn btn--primary" disabled={submitting}>
                                        {submitting ? 'Adding…' : 'Add GitHub repo'}
                                    </button>
                                )}
                            </footer>
                        </form>
                    ) : (
                        <form onSubmit={handleDriveSubmit}>
                            {driveLoading && <div className="form-hint">Checking service account…</div>}
                            {driveError && (
                                <div className="form-error">
                                    {driveError}
                                </div>
                            )}
                            {drive && (
                                <div className="form-group form-callout">
                                    {drive.dwd ? (
                                        <>
                                            <span className="form-label">Indexer access</span>
                                            <p className="form-hint">
                                                The indexer is configured to impersonate a Workspace user via
                                                Domain-Wide Delegation. If indexing fails with a 403 after you add
                                                this source, verify the service account is authorized in
                                                Workspace Admin Console for the <code>drive.readonly</code> scope
                                                and that the impersonation user can see this folder.
                                            </p>
                                            <details>
                                                <summary>Service-account email (fallback sharing)</summary>
                                                <p className="form-hint">
                                                    If DWD authorization is missing, the indexer falls back to
                                                    needing per-folder shares with:
                                                </p>
                                                <code className="form-value-block">{drive.serviceAccountEmail}</code>
                                            </details>
                                        </>
                                    ) : (
                                        <>
                                            <span className="form-label">Share folders with the service account</span>
                                            <p className="form-hint">
                                                Domain-Wide Delegation is not configured, so the indexer can only
                                                read folders explicitly shared with this email (Viewer access is
                                                enough):
                                            </p>
                                            <code className="form-value-block">{drive.serviceAccountEmail}</code>
                                        </>
                                    )}
                                    {drive.hint && <p className="form-hint">{drive.hint}</p>}
                                </div>
                            )}
                            <label className="form-group">
                                <span className="form-label">Alias</span>
                                <input
                                    type="text"
                                    value={driveAlias}
                                    onChange={(e) => setDriveAlias(e.target.value)}
                                    placeholder="finance-docs"
                                    autoFocus
                                    required
                                />
                                <span className="form-hint">
                                    A short, URL-safe slug. The source will live at <code>gdrive/{driveAlias || '<alias>'}</code>.
                                </span>
                            </label>
                            {pickerEnabled ? (
                                <div className="form-group form-callout">
                                    <span className="form-label">Pick the folder</span>
                                    <p className="form-hint">
                                        Sign in with your Google account to browse your My Drive and Shared Drives.
                                        Indexing then happens via the BACR service account — your sign-in is used only
                                        for this picker.
                                    </p>
                                    <div className="grant-row">
                                        <button
                                            type="button"
                                            className="btn btn--primary btn--small"
                                            onClick={() => void launchPicker()}
                                            disabled={pickerLaunching}
                                        >
                                            {pickerLaunching ? 'Opening Drive…' : 'Pick from Google Drive…'}
                                        </button>
                                        {driveFolderId && (
                                            <span className="form-hint">
                                                Selected: <code>{driveFolderName || driveFolderId}</code>
                                            </span>
                                        )}
                                    </div>
                                    {pickerError && <div className="form-error">{pickerError}</div>}
                                </div>
                            ) : null}
                            <label className="form-group">
                                <span className="form-label">
                                    {pickerEnabled ? 'Drive folder ID (auto-filled)' : 'Drive folder ID'}
                                </span>
                                <input
                                    type="text"
                                    value={driveFolderId}
                                    onChange={(e) => {
                                        setDriveFolderId(e.target.value);
                                        // Manual ID edit invalidates the previously-auto-filled
                                        // display name (it was for a different folder). Clear it
                                        // unless the admin explicitly typed a name themselves.
                                        if (!driveFolderNameDirty) setDriveFolderName('');
                                    }}
                                    placeholder="1AbcDEF…"
                                    required
                                />
                                <span className="form-hint">
                                    {pickerEnabled
                                        ? <>Filled in when you pick a folder above. You can also paste a folder ID directly.</>
                                        : <>From a folder URL <code>drive.google.com/drive/folders/&lt;ID&gt;</code> — copy the <strong>&lt;ID&gt;</strong> segment.</>}
                                </span>
                            </label>
                            <details className="form-group">
                                <summary>
                                    {pickerEnabled
                                        ? 'Browse subfolders by folder ID (fallback when the Picker is unavailable)'
                                        : 'Browse subfolders (optional)'}
                                </summary>
                                <div className="form-group">
                                    <span className="form-label">Browse subfolders</span>
                                    <div className="grant-row">
                                        <input
                                            type="text"
                                            value={browseRoot}
                                            placeholder="Paste a parent folder ID to list children"
                                            onChange={(e) => setBrowseRoot(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn--ghost btn--small"
                                            onClick={() => void browseSubfolders()}
                                            disabled={browseLoading || !browseRoot.trim()}
                                        >
                                            {browseLoading ? 'Loading…' : 'List subfolders'}
                                        </button>
                                    </div>
                                    {browseError && <div className="form-error">{browseError}</div>}
                                    {browseListing && (
                                        <ul className="grant-list">
                                            {browseListing.folders.length === 0 && (
                                                <li className="form-hint">No subfolders.</li>
                                            )}
                                            {browseListing.folders.map((f) => (
                                                <li key={f.id}>
                                                    <button
                                                        type="button"
                                                        className="btn btn--ghost btn--small"
                                                        onClick={() => {
                                                            setDriveFolderId(f.id);
                                                            if (!driveFolderNameDirty) setDriveFolderName(f.name);
                                                        }}
                                                    >
                                                        {f.name}
                                                    </button>
                                                    {f.registeredRepoId && (
                                                        <span className="form-hint">
                                                            {' '}— already registered as <code>{f.registeredRepoId}</code>
                                                        </span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </details>
                            <label className="form-group">
                                <span className="form-label">Display name (optional)</span>
                                <input
                                    type="text"
                                    value={driveFolderName}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setDriveFolderName(v);
                                        // Clearing the field opts back into auto-fill on the
                                        // next folder pick. Any non-empty edit is sticky.
                                        setDriveFolderNameDirty(v.trim().length > 0);
                                    }}
                                    placeholder="Finance docs"
                                />
                            </label>
                            <fieldset className="form-group">
                                <legend className="form-label">Doc types to index</legend>
                                <label className="form-group form-group--inline">
                                    <input
                                        type="checkbox"
                                        checked={docTypeFilters.includes('all')}
                                        onChange={() => toggleDocType('all')}
                                    />
                                    <span><strong>All supported types</strong> (recommended)</span>
                                </label>
                                {ALL_DOC_TYPES.map((t) => (
                                    <label key={t.key} className="form-group form-group--inline">
                                        <input
                                            type="checkbox"
                                            disabled={docTypeFilters.includes('all')}
                                            checked={docTypeFilters.includes(t.key)}
                                            onChange={() => toggleDocType(t.key)}
                                        />
                                        <span>{t.label} <span className="form-hint">— {t.hint}</span></span>
                                    </label>
                                ))}
                            </fieldset>
                            <SensitiveLockToggle value={sensitiveContentLock} onChange={setSensitiveContentLock} />
                            {error && <div className="form-error">{error}</div>}
                            {success && <div className="form-success">{success}</div>}
                            <footer className="modal-footer">
                                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
                                    {success ? 'Close' : 'Cancel'}
                                </button>
                                {!success && (
                                    <button type="submit" className="btn btn--primary" disabled={submitting}>
                                        {submitting ? 'Adding…' : 'Add Drive folder'}
                                    </button>
                                )}
                            </footer>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

function SensitiveLockToggle({ value, onChange }: { value: boolean; onChange: (next: boolean) => void }) {
    return (
        <label className="form-group form-group--inline">
            <input
                type="checkbox"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span>Sensitive-content lock (require admin approval before filing issues with screenshots)</span>
        </label>
    );
}
